import './Architecture.css';

const LAYERS = [
  {
    tier: 'L1',
    name: 'Interface',
    note: 'What the user actually touches',
    nodes: ['React / Next.js', 'Design system', 'Accessibility', 'Analytics'],
  },
  {
    tier: 'L2',
    name: 'Edge',
    note: 'Before a request reaches your servers',
    nodes: ['CDN + cache', 'Auth · JWT · OAuth', 'Rate limiting', 'Input validation'],
  },
  {
    tier: 'L3',
    name: 'Services',
    note: 'Business logic and orchestration',
    nodes: ['REST / GraphQL API', 'Background jobs', 'Webhooks', 'Payments — Stripe'],
    accent: true,
  },
  {
    tier: 'L4',
    name: 'Data',
    note: 'The part you cannot afford to lose',
    nodes: ['PostgreSQL', 'MongoDB', 'Redis cache', 'Object storage', 'Backups'],
  },
  {
    tier: 'L5',
    name: 'Platform',
    note: 'How it ships and how it stays up',
    nodes: ['Docker / K8s', 'CI/CD pipelines', 'Observability', 'Hardened Linux'],
  },
];

const PRINCIPLES = [
  {
    n: '01',
    t: 'Design for the second year',
    d: 'Boundaries, contracts and naming chosen so the codebase is still cheap to change after the third pivot. Most software dies of accumulated shortcuts, not of missing features.',
  },
  {
    n: '02',
    t: 'Cost is an architecture decision',
    d: 'Caching, queueing and the right storage tier keep the cloud bill flat while traffic grows. Measured every month, not assumed at launch.',
  },
  {
    n: '03',
    t: 'Ship it, then watch it',
    d: 'Logs, metrics and alerts on day one, so a problem surfaces on a dashboard before a customer has to describe it in an email.',
  },
  {
    n: '04',
    t: 'Security is not a phase',
    d: 'Least privilege, scoped keys, secret hygiene, hardened servers and audited dependencies — built in, never bolted on the week before launch.',
  },
];

export default function Architecture() {
  return (
    <section id="architecture" className="band arch">
      <div className="shell">

        <div className="sec-head" data-rv="up">
          <span className="sec-tag"><b>02</b> — Systems</span>
          <h2 className="display-lg">
            <span className="mask-line"><span>Anyone can build</span></span>
            <span className="mask-line" style={{ '--ml-d': '110ms' }}><span>a demo.</span></span>
          </h2>
          <p className="lede">
            The difference between a prototype and a product is everything underneath it —
            auth, payments, backups, deploys, monitoring, cost. Every project I take on is
            mapped against this before a line is written.
          </p>
        </div>

        <div className="arch-body">

          {/* ── Diagram ────────────────────────────────────── */}
          <div className="arch-diagram" data-rv="fade">
            <div className="arch-diagram-head">
              <span className="mono">reference_topology</span>
              <span className="arch-flow-tag"><i className="pulse" />request flow</span>
            </div>

            {LAYERS.map((layer, i) => (
              <div
                className={`arch-layer ${layer.accent ? 'arch-layer--accent' : ''}`}
                key={layer.tier}
                data-rv="up"
                style={{ '--rv-d': `${i * 90}ms` }}
              >
                <div className="arch-layer-meta">
                  <span className="arch-tier">{layer.tier}</span>
                  <span className="arch-name">{layer.name}</span>
                  <span className="arch-note">{layer.note}</span>
                </div>

                <div className="arch-nodes">
                  {layer.nodes.map((node) => (
                    <span className="arch-node" key={node}>
                      <i className="arch-node-dot" />
                      {node}
                    </span>
                  ))}
                </div>

                {i < LAYERS.length - 1 && (
                  <span className="arch-conn" aria-hidden="true">
                    <i className="arch-packet" style={{ animationDelay: `${i * 0.45}s` }} />
                  </span>
                )}
              </div>
            ))}

            <div className="arch-diagram-foot">
              <span className="mono">p95 &lt; 200ms</span>
              <span className="mono">stateless services</span>
              <span className="mono">zero-downtime deploys</span>
            </div>
          </div>

          {/* ── Principles ─────────────────────────────────── */}
          <div className="arch-principles">
            <span className="mono arch-principles-label">Operating principles</span>
            {PRINCIPLES.map((p, i) => (
              <article
                className="arch-principle"
                key={p.n}
                data-rv="left"
                style={{ '--rv-d': `${140 + i * 100}ms` }}
              >
                <span className="arch-principle-n">{p.n}</span>
                <h3 className="arch-principle-t">{p.t}</h3>
                <p className="arch-principle-d">{p.d}</p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
